import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import styles from './SpellingModes.module.css';
import AITutor from '../AITutor/AITutor';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL, apiRequest } from '../../config/api';
import Loader from '../common/Loader/Loader';

const StorySpellingMode = () => {
  const { userProgress, loading: userLoading, error: userError, updateProgress } = useUser();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [story, setStory] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const initialized = useRef(false);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [questionOrder, setQuestionOrder] = useState([]);
  const [completingSession, setCompletingSession] = useState(false);

  const navigate = useNavigate();
  const { state } = useLocation();
  const difficulty = state?.difficulty || userProgress?.modeLevels?.story_spelling || 1;

  useEffect(() => {
    if (!userLoading && userProgress && !initialized.current) {
      startPractice();
      initialized.current = true;
    }
  }, [userLoading, userProgress]);

  useEffect(() => {
    if (questions.length > 0) {
      initializeQuestion();
    }
  }, [currentQuestion, questions]);

  const initializeQuestion = () => {
    setQuestionStartTime(Date.now());
    setUserInput('');
    setShowExplanation(false);
    setFeedback(null);
  };

  const startPractice = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        mode: 'story_spelling',
        difficulty: difficulty
      });

      const data = await apiRequest(`/spelling/story?${params}`);
      setStory(data.story);
      setQuestions(data.questions);
      
      // Create randomized order of question indices
      const indices = Array.from({ length: data.questions.length }, (_, i) => i);
      shuffleArray(indices);
      setQuestionOrder(indices);

      const sessionData = await apiRequest('/sessions/start', {
        method: 'POST',
        body: JSON.stringify({
          mode: 'story_spelling',
          difficulty: difficulty,
          questions: data.questions.map(q => q._id)
        })
      });

      localStorage.setItem('currentSessionId', sessionData._id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Add a shuffle function
  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  const renderStoryWithGaps = () => {
    if (!story || !questions.length || questionOrder.length === 0) return null;
    
    // First, create a temporary div to parse the HTML content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = story.content;
    
    // Find all blank spans and update their classes
    const spans = tempDiv.getElementsByClassName('blank');
    
    // Reset all spans to 'blank' class first
    Array.from(spans).forEach(span => {
      span.className = 'blank';
    });
    
    // Only set the active blank if we have a valid current question index
    if (currentQuestion >= 0 && currentQuestion < questionOrder.length) {
      // Get the index of the question we're currently on from our randomized order
      const currentQuestionIndex = questionOrder[currentQuestion];
      
      // Make sure we don't exceed the number of spans
      if (currentQuestionIndex < spans.length) {
        spans[currentQuestionIndex].className = 'activeBlank';
      }
    }
    
    return <div 
      dangerouslySetInnerHTML={{ __html: tempDiv.innerHTML }}
      style={{ lineHeight: '1.4' }}  
    />;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !userInput.trim()) return;
    setIsSubmitting(true);

    try {
      const sessionId = localStorage.getItem('currentSessionId');
      // Use the randomized order to get the current question
      const currentQ = questions[questionOrder[currentQuestion]];
      const timeSpent = (Date.now() - questionStartTime) / 1000;
      
      const result = await apiRequest(`/sessions/${sessionId}/answer`, {
        method: 'POST',
        body: JSON.stringify({
          questionId: currentQ._id,
          answer: userInput.trim(),
          timeSpent
        })
      });

      setFeedback({
        isCorrect: result.isCorrect,
        message: result.isCorrect ? 'Correct!' : 'Incorrect. Try again!'
      });
      setShowExplanation(true);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    setShowExplanation(false);
    
    if (currentQuestion + 1 >= questions.length) {
      try {
        setCompletingSession(true);
        const sessionId = localStorage.getItem('currentSessionId');
        
        const results = await apiRequest(`/sessions/${sessionId}/end`, {
          method: 'POST'
        });

        localStorage.removeItem('currentSessionId');
        updateProgress(results);
        
        // Navigate to results page with session results and questions
        navigate('/results', { 
          state: { 
            sessionResults: results,
            questions: questions
          } 
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setCompletingSession(false);
      }
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  if (loading || userLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  // Use the randomized order to get the current question
  const currentQ = questionOrder.length > 0 ? questions[questionOrder[currentQuestion]] : null;
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className={styles.container}>
      {completingSession && (
        <div className={styles.overlay}>
          <Loader size="large" text="Analyzing your results..." />
        </div>
      )}
      <div className={styles.card}>
        <AITutor 
          currentQuestion={currentQ}
          userAnswer={userInput}
          isCorrect={feedback?.isCorrect}
        />
        
        <div className={styles.header}>
          <h2 className={styles.title}>Read the story below and rewrite each word with blanks.</h2>
          <span className={styles.questionCount}>
            Word {currentQuestion + 1} of {questions.length}
          </span>
        </div>

        <div className={styles.progressContainer}>
          <div className={styles.progressBar} style={{ width: `${progress}%` }} />
        </div>

        <div className={styles.storyContainer}>
          {renderStoryWithGaps()}
        </div>

        <div className={styles.questionContent}>
          {currentQ?.content.hints?.map((hint, index) => (
            <div key={index} className={styles.hint}>{hint}</div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className={styles.inputForm}>
          <input
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            className={styles.answerInput}
            placeholder="Fill in the missing word..."
            disabled={showExplanation}
          />
          {!showExplanation && (
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={isSubmitting || !userInput.trim()}
            >
              Submit
            </button>
          )}
        </form>

        {feedback && (
          <div className={`${styles.feedback} ${feedback.isCorrect ? styles.correct : styles.incorrect}`}>
            {feedback.message}
          </div>
        )}

        {showExplanation && (
          <>
            <div className={styles.explanation}>
              <p>Correct word: {currentQ.content.correctAnswer}</p>
              <p>{currentQ.content.explanation}</p>
              <div className={styles.wordInfo}>
                <p>Definition: {currentQ.content.definition}</p>
                <p>Usage: {currentQ.content.usageExample}</p>
              </div>
            </div>
            <button 
              className={styles.nextButton}
              onClick={handleNext}
            >
              {currentQuestion + 1 >= questions.length ? 'Finish Story' : 'Next Word'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default StorySpellingMode;